import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTicketService } from '../services/ticketService';
import { ticketKeys } from './useTicketList';

/** A single ticket's detail (notes, description, resolution). Cached per
 *  customer + ticket so reopening one is instant and revalidated in the
 *  background. */
export function useTicket(customerId: string | undefined, ticketId: string | null) {
  const ticketService = useTicketService();

  return useQuery({
    queryKey: ticketKeys.detail(customerId, ticketId),
    queryFn: () => ticketService.getTicket(customerId as string, ticketId as string),
    enabled: !!customerId && !!ticketId,
  });
}

/** Adds a private note to a ticket, then invalidates that ticket's detail so
 *  the new note is refetched. */
export function useAddTicketNote() {
  const ticketService = useTicketService();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ customerId, ticketId, body }: { customerId: string; ticketId: string; body: string }) =>
      ticketService.addNote(customerId, ticketId, body),
    onSuccess: (_data, { customerId, ticketId }) => {
      queryClient.invalidateQueries({ queryKey: ticketKeys.detail(customerId, ticketId) });
    },
  });
}
